import { Injectable, inject, signal, computed } from '@angular/core';
import { forkJoin, Observable, firstValueFrom, of } from 'rxjs';
import { tap, map, switchMap } from 'rxjs/operators';
import {
  PracticeRepositoryService,
  PracticeSettingsRepositoryService,
  InviteRepositoryService,
  PracticeProcedureRepositoryService,
  PatientRepositoryService,
  PatientListsRepositoryService,
  PatientAppointmentsRepositoryService,
  UserRepositoryService,
  PracticeLetterheadRepositoryService,
  PatientStatusRepositoryService,
  Practice,
  PracticeSettings,
  Invite,
  PracticeProcedure,
  PatientStatus,
  PatientFlagEntity,
  AppointmentType,
  Letterhead,
  PracticeUser,
  PracticeLocation,
  PracticePractitioner,
  LocalStorageService,
  WebSocketService,
  Events, StateStore, StateStorePromises
} from '@xmedical/core';
import { CacheService } from '../services/cache.service';
import {CoreEvent, CoreEventsService} from '../services/core-events.service';
import {AuthenticationService} from '../auth/authentication.service';
import { UploadsStore } from '@xmedical/uploads';

/**
 * PracticeStore
 *
 * Manages practice-specific data that changes when user switches practices.
 * Uses Angular signals for reactive state management.
 *
 * Data managed:
 * - Available practices
 * - Selected practice
 * - Practice invites
 * - Practice settings
 * - Appointment types
 * - Practice procedures
 * - Letterheads
 * - Patient statuses
 * - Patient flags
 * - Practice user profile
 *
 * All data is cached in localStorage and reloaded when practice changes.
 * Subscribes to WebSocket events for real-time updates.
 * Each store registers its own boot steps for initialization tracking.
 */
@Injectable({
  providedIn: 'root'
})
export class PracticeStore implements StateStore, StateStorePromises {
  private practiceRepo = inject(PracticeRepositoryService);
  private practiceSettingsRepo = inject(PracticeSettingsRepositoryService);
  private inviteRepo = inject(InviteRepositoryService);
  private procedureRepo = inject(PracticeProcedureRepositoryService);
  private patientRepo = inject(PatientRepositoryService);
  private patientStatusRepo = inject(PatientStatusRepositoryService);
  private patientListsRepo = inject(PatientListsRepositoryService);
  private appointmentsRepo = inject(PatientAppointmentsRepositoryService);
  private letterheadRepo = inject(PracticeLetterheadRepositoryService);
  private userRepo = inject(UserRepositoryService);
  private cache = inject(CacheService);
  private events = inject(CoreEventsService);
  private auth = inject(AuthenticationService);
  private websocket = inject(WebSocketService);
  private localStorage = inject(LocalStorageService);
  private uploadsStore = inject(UploadsStore);

  // State signals
  readonly practices = signal<Practice[]>([]);
  readonly selectedPractice = signal<Practice | null>(null);
  readonly invites = signal<Invite[]>([]);
  readonly practiceSettings = signal<PracticeSettings | null>(null);
  readonly appointmentTypes = signal<AppointmentType[]>([]);
  readonly practiceProcedures = signal<PracticeProcedure[]>([]);
  readonly letterheads = signal<Letterhead[]>([]);
  readonly patientStatuses = signal<PatientStatus[]>([]);
  readonly patientFlags = signal<PatientFlagEntity[]>([]);
  readonly practiceUser = signal<PracticeUser | null>(null);
  readonly practiceLocations = signal<PracticeLocation[]>([]);
  readonly practicePractitioners = signal<PracticePractitioner[]>([]);
  readonly loaded = signal<boolean>(false);
  readonly loading = signal<boolean>(false);

  // Promise for tracking in-flight load operation
  loadingPromise: Promise<void> | null = null;

  // Computed signals
  readonly hasPractices = computed(() => this.practices().length > 0);
  readonly hasInvites = computed(() => this.invites().length > 0);
  readonly selectedPracticeId = computed(() => this.selectedPractice()?.id ?? null);
  readonly practiceEventsChannelName = computed(() =>
    `xmedical_practice_${this.selectedPracticeId()}_events`
  );
  readonly practicePatientEventsChannelName = computed(() =>
    `xmedical_practice_${this.selectedPracticeId()}_patient_events`
  );

  /**
   * Initialize practice service
   * Loads practices, selects default, and loads invites
   *
   * @returns Observable that completes when initialized
   */
  initialize(): Observable<void> {
    this.loading.set(true);

    return forkJoin({
      practices: this.loadPractices(),
      invites: this.loadInvites()
    }).pipe(
      tap(({ practices }) => {
        // Select default practice
        this.selectDefaultPractice(practices);
      }),
      switchMap(() => {
        if (this.selectedPractice()) {
          return this.loadPracticeData();
        }
        return of(void 0);
      })
    );
  }

  /**
   * Load all practices for current user
   */
  private loadPractices(): Observable<Practice[]> {
    return this.cache.cacheFirst(
      'practices',
      () => this.practiceRepo.getAllPractices(),
      {
        onCacheHit: (practices) => {
          this.practices.set(practices);
        },
        onFetchComplete: (practices) => {
          this.practices.set(practices);
        }
      }
    );
  }

  /**
   * Load invites for current user
   */
  private loadInvites(): Observable<Invite[]> {
    return this.inviteRepo.getAllInvites().pipe(
      tap(invites => {
        this.invites.set(invites);
        this.events.trigger(new CoreEvent('invites_loaded'));
      })
    );
  }

  /**
   * Select default practice from cache or first practice
   */
  private selectDefaultPractice(practices: Practice[]): void {
    const cachedPractice = this.localStorage.getItem<Practice>('selected_practice', null);

    if (cachedPractice && practices.some(p => p.id === cachedPractice.id)) {
      this.selectedPractice.set(cachedPractice);
    } else if (practices.length > 0) {
      this.selectedPractice.set(practices[0]);
      this.localStorage.setItem('selected_practice', practices[0]);
    }

    this.events.trigger(new CoreEvent('practice_loaded'));
  }

  /**
   * Select a practice and reload all practice-specific data
   *
   * @param practiceId - ID of practice to select
   */
  selectPractice(practiceId: number): void {
    const practice = this.practices().find(p => p.id === practiceId);
    if (!practice) {
      console.error(`[PracticeStore] Practice with ID ${practiceId} not found`);
      return;
    }

    // Clear old practice data
    this.clearPracticeData();

    // Set new practice
    this.selectedPractice.set(practice);
    this.localStorage.setItem('selected_practice', practice);

    // Trigger event
    this.events.trigger(new CoreEvent('practice_change', { practiceId }));

    // Setup websocket for new practice
    this.resetPracticePushService();
    this.setupPracticePushService();

    // Load practice-specific data (UserStore and TemplatesStore listen to practice_change event)
    this.loadPracticeData().subscribe();
  }

  /**
   * Load all practice-specific data
   */
  private loadPracticeData(): Observable<void> {
    this.loading.set(true);

    return forkJoin({
      settings: this.loadPracticeSettings(),
      procedures: this.loadProcedures(),
      statuses: this.loadPatientStatuses(),
      flags: this.loadPatientFlags(),
      letterheads: this.loadLetterheads(),
      appointmentTypes: this.loadAppointmentTypes(),
      practiceUser: this.loadPracticeUserProfile(),
      practiceLocations: this.loadPracticeLocations(),
      practicePractitioners: this.loadPracticePractitioners()
    }).pipe(
      tap(() => {
        this.loaded.set(true);
        this.loading.set(false);
      }),
      map(() => void 0)
    );
  }

  /**
   * Ensure practice data is loaded - on-demand loading for page refresh scenarios.
   * - If already loaded, returns immediately
   * - If loading in progress, waits for completion
   * - If not loaded, triggers fetch and waits
   *
   * @returns Promise that resolves when practice data is available
   */
  async ensureLoaded(): Promise<void> {
    if (this.loaded()) return;

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Not loaded and not loading - trigger initialization or practice data load
    if (this.hasPractices() && this.selectedPractice()) {
      // Practices loaded but practice-specific data not loaded
      this.loadingPromise = firstValueFrom(this.loadPracticeData()).finally(() => {
        this.loadingPromise = null;
      });
    } else {
      // Nothing loaded - full initialization needed
      this.loadingPromise = firstValueFrom(this.initialize()).finally(() => {
        this.loadingPromise = null;
      });
    }

    return this.loadingPromise;
  }

  /**
   * Load practice settings
   */
  private loadPracticeSettings(): Observable<PracticeSettings> {
    return this.cache.cacheFirst(
      'practice_settings',
      () => this.practiceSettingsRepo.load(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (settings) => {
          this.practiceSettings.set(settings);
          this.events.trigger(new CoreEvent('practice_settings_loaded'));

        },
        onFetchComplete: (settings) => {
          this.practiceSettings.set(settings);
          this.events.trigger(new CoreEvent('practice_settings_loaded'));

        }
      }
    );
  }

  /**
   * Load practice procedures
   */
  private loadProcedures(): Observable<PracticeProcedure[]> {
    return this.cache.cacheFirst(
      'practice_procedures',
      () => this.procedureRepo.loadAll(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (procedures) => {
          this.practiceProcedures.set(procedures);
          this.events.trigger(new CoreEvent('practice_procedures_loaded'));

        },
        onFetchComplete: (procedures) => {
          this.practiceProcedures.set(procedures);
          this.events.trigger(new CoreEvent('practice_procedures_loaded'));

        }
      }
    );
  }

  /**
   * Load patient statuses
   */
  private loadPatientStatuses(): Observable<PatientStatus[]> {
    return this.cache.cacheFirst(
      'patient_statuses',
      () => this.patientStatusRepo.loadStatuses(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (statuses) => {
          this.patientStatuses.set(statuses);
          this.events.trigger(new CoreEvent('patient_statuses_loaded'));

        },
        onFetchComplete: (statuses) => {
          this.patientStatuses.set(statuses);
          this.events.trigger(new CoreEvent('patient_statuses_loaded'));

        }
      }
    );
  }

  /**
   * Load patient flags
   */
  private loadPatientFlags(): Observable<PatientFlagEntity[]> {
    return this.cache.cacheFirst(
      'patient_flags',
      () => this.patientListsRepo.loadLists(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (flags) => {
          this.patientFlags.set(flags);
          this.events.trigger(new CoreEvent('patient_flags_loaded'));

        },
        onFetchComplete: (flags) => {
          this.patientFlags.set(flags);
          this.events.trigger(new CoreEvent('patient_flags_loaded'));

        }
      }
    );
  }

  /**
   * Load practice letterheads
   */
  private loadLetterheads(): Observable<Letterhead[]> {
    return this.cache.cacheFirst(
      'practice_letterheads',
      () => this.letterheadRepo.loadAll(),
      {
        userId: this.auth.getUserId(),
        onCacheHit: (letterheads) => {
          this.letterheads.set(letterheads);
          this.events.trigger(new CoreEvent('practice_letterheads_loaded'));

        },
        onFetchComplete: (letterheads) => {
          this.letterheads.set(letterheads);
          this.events.trigger(new CoreEvent('practice_letterheads_loaded'));

        }
      }
    );
  }

  /**
   * Load appointment types
   */
  private loadAppointmentTypes(): Observable<AppointmentType[]> {
    return this.appointmentsRepo.loadAppointmentTypes().pipe(
      tap(types => {
        this.appointmentTypes.set(types);
        this.events.trigger(new CoreEvent('appointment_types_loaded'));
      })
    );
  }

  /**
   * Load practice user profile
   */
  private loadPracticeUserProfile(): Observable<PracticeUser> {
    return this.userRepo.loadCurrentPracticeUserProfile().pipe(
      tap(practiceUser => {
        this.practiceUser.set(practiceUser);
        this.events.trigger(new CoreEvent('practice_user_profile_loaded'));
      })
    );
  }

  /**
   * Load practice locations
   */
  private loadPracticeLocations(): Observable<PracticeLocation[]> {
    const practiceId = this.selectedPracticeId();
    return this.practiceRepo.getAllPracticeLocations(practiceId).pipe(
      tap(locations => {
        this.practiceLocations.set(locations);
      })
    );
  }

  /**
   * Load practice practitioners
   */
  private loadPracticePractitioners(): Observable<PracticePractitioner[]> {
    const practiceId = this.selectedPracticeId();
    return this.practiceRepo.getAllPracticePractitioners(practiceId).pipe(
      tap(practitioners => {
        this.practicePractitioners.set(practitioners);
      })
    );
  }

  /**
   * Update practice settings
   *
   * @param settings - Updated settings
   * @returns Observable that completes when settings are updated
   */
  updatePracticeSettings(settings: PracticeSettings): Observable<boolean> {
    return this.practiceSettingsRepo.adminUpdate(settings).pipe(
      tap(success => {
        if (success) {
          this.practiceSettings.set(settings);
          // Update cache
          const userId = this.auth.getUserId();
          this.cache.set(`${userId}-practice_settings`, settings);
          this.events.trigger(new CoreEvent('practice_settings_loaded'));
        }
      })
    );
  }

  /**
   * Create patient flag
   *
   * @param flag - New flag to create
   * @returns Observable that completes when flag is created, returns the new flag ID
   */
  createPatientFlag(flag: PatientFlagEntity): Observable<number> {
    return this.patientListsRepo.adminCreate(flag).pipe(
      tap(id => {
        // Add the new flag to the signal
        flag.id = id;
        const flags = this.patientFlags();
        const newFlags = [...flags, flag].sort((a, b) => a.id - b.id);
        this.patientFlags.set(newFlags);

        // Update cache
        const userId = this.auth.getUserId();
        this.cache.set(`${userId}-patient_flags`, newFlags);
        this.events.trigger(new CoreEvent('patient_flags_loaded'));
      })
    );
  }

  /**
   * Update patient flag
   *
   * @param flag - Updated flag
   * @returns Observable that completes when flag is updated
   */
  updatePatientFlag(flag: PatientFlagEntity): Observable<boolean> {
    return this.patientListsRepo.adminUpdate(flag.id, flag).pipe(
      tap(success => {
        if (success) {
          // Update signal: replace the flag in the array
          const flags = this.patientFlags();
          const index = flags.findIndex(f => f.id === flag.id);
          if (index > -1) {
            const newFlags = [...flags];
            newFlags[index] = flag;
            this.patientFlags.set(newFlags);

            // Update cache
            const userId = this.auth.getUserId();
            this.cache.set(`${userId}-patient_flags`, newFlags);
            this.events.trigger(new CoreEvent('patient_flags_loaded'));
          }
        }
      })
    );
  }

  /**
   * Delete patient flag
   *
   * @param id - Flag ID to delete
   * @returns Observable that completes when flag is deleted
   */
  deletePatientFlag(id: number): Observable<boolean> {
    return this.patientListsRepo.adminDelete(id).pipe(
      tap(success => {
        if (success) {
          // Remove the flag from the signal
          const flags = this.patientFlags();
          const newFlags = flags.filter(f => f.id !== id);
          this.patientFlags.set(newFlags);

          // Update cache
          const userId = this.auth.getUserId();
          this.cache.set(`${userId}-patient_flags`, newFlags);
          this.events.trigger(new CoreEvent('patient_flags_loaded'));
        }
      })
    );
  }

  /**
   * Create letterhead
   *
   * @param letterhead - New letterhead to create
   * @returns Observable that completes when letterhead is created, returns the new letterhead ID
   */
  createLetterhead(letterhead: Letterhead): Observable<number> {
    return this.letterheadRepo.adminCreate(letterhead).pipe(
      tap(id => {
        // Add the new letterhead to the signal
        letterhead.id = id;
        const letterheads = this.letterheads();
        const newLetterheads = [...letterheads, letterhead];
        this.letterheads.set(newLetterheads);

        // Update cache
        const userId = this.auth.getUserId();
        this.cache.set(`${userId}-practice_letterheads`, newLetterheads);
        this.events.trigger(new CoreEvent('practice_letterheads_loaded'));
      })
    );
  }

  /**
   * Update letterhead
   *
   * @param letterhead - Updated letterhead
   * @returns Observable that completes when letterhead is updated
   */
  updateLetterhead(letterhead: Letterhead): Observable<boolean> {
    return this.letterheadRepo.adminUpdate(letterhead).pipe(
      tap(success => {
        if (success) {
          // Update signal: replace the letterhead in the array
          const letterheads = this.letterheads();
          const index = letterheads.findIndex(l => l.id === letterhead.id);
          if (index > -1) {
            const newLetterheads = [...letterheads];
            newLetterheads[index] = letterhead;
            this.letterheads.set(newLetterheads);

            // Update cache
            const userId = this.auth.getUserId();
            this.cache.set(`${userId}-practice_letterheads`, newLetterheads);
            this.events.trigger(new CoreEvent('practice_letterheads_loaded'));
          }
        }
      })
    );
  }

  /**
   * Delete letterhead
   *
   * @param id - Letterhead ID to delete
   * @returns Observable that completes when letterhead is deleted
   */
  deleteLetterhead(id: number): Observable<boolean> {
    return this.letterheadRepo.adminDelete(id).pipe(
      tap(success => {
        if (success) {
          // Remove the letterhead from the signal
          const letterheads = this.letterheads();
          const newLetterheads = letterheads.filter(l => l.id !== id);
          this.letterheads.set(newLetterheads);

          // Update cache
          const userId = this.auth.getUserId();
          this.cache.set(`${userId}-practice_letterheads`, newLetterheads);
          this.events.trigger(new CoreEvent('practice_letterheads_loaded'));
        }
      })
    );
  }

  /**
   * Create patient status
   *
   * @param status - New status to create
   * @returns Observable that completes when status is created, returns the new status ID
   */
  createPatientStatus(status: PatientStatus): Observable<number> {
    return this.patientStatusRepo.adminCreate(status).pipe(
      tap(id => {
        // Add the new status to the signal
        status.id = id;
        const statuses = this.patientStatuses();
        const newStatuses = [...statuses, status].sort((a, b) => a.id - b.id);
        this.patientStatuses.set(newStatuses);

        // Update cache
        const userId = this.auth.getUserId();
        this.cache.set(`${userId}-patient_statuses`, newStatuses);
        this.events.trigger(new CoreEvent('patient_statuses_loaded'));
      })
    );
  }

  /**
   * Update patient status
   *
   * @param status - Updated status
   * @returns Observable that completes when status is updated
   */
  updatePatientStatus(status: PatientStatus): Observable<boolean> {
    return this.patientStatusRepo.adminUpdate(status.id, status).pipe(
      tap(success => {
        if (success) {
          // Update signal: replace the status in the array
          const statuses = this.patientStatuses();
          const index = statuses.findIndex(s => s.id === status.id);
          if (index > -1) {
            const newStatuses = [...statuses];
            newStatuses[index] = status;
            this.patientStatuses.set(newStatuses);

            // Update cache
            const userId = this.auth.getUserId();
            this.cache.set(`${userId}-patient_statuses`, newStatuses);
            this.events.trigger(new CoreEvent('patient_statuses_loaded'));
          }
        }
      })
    );
  }

  /**
   * Delete patient status
   *
   * @param id - Status ID to delete
   * @returns Observable that completes when status is deleted
   */
  deletePatientStatus(id: number): Observable<boolean> {
    return this.patientStatusRepo.adminDelete(id).pipe(
      tap(success => {
        if (success) {
          // Remove the status from the signal
          const statuses = this.patientStatuses();
          const newStatuses = statuses.filter(s => s.id !== id);
          this.patientStatuses.set(newStatuses);

          // Update cache
          const userId = this.auth.getUserId();
          this.cache.set(`${userId}-patient_statuses`, newStatuses);
          this.events.trigger(new CoreEvent('patient_statuses_loaded'));
        }
      })
    );
  }

  /**
   * Clear practice-specific data
   */
  clearPracticeData(): void {
    this.practiceSettings.set(null);
    this.appointmentTypes.set([]);
    this.practiceProcedures.set([]);
    this.letterheads.set([]);
    this.patientStatuses.set([]);
    this.patientFlags.set([]);
    this.practiceUser.set(null);
    this.practiceLocations.set([]);
    this.practicePractitioners.set([]);
    this.loaded.set(false);

    // Clear uploads store cache
    this.uploadsStore.clear();

    // Clear cache
    const userId = this.auth.getUserId();
    this.cache.invalidate('practice_settings', userId);
    this.cache.invalidate('practice_procedures', userId);
    this.cache.invalidate('patient_statuses', userId);
    this.cache.invalidate('patient_flags', userId);
    this.cache.invalidate('practice_letterheads', userId);
  }

  /**
   * Setup WebSocket subscriptions for practice events
   */
  private setupPracticePushService(): void {
    const channelName = this.practiceEventsChannelName();

    // Subscribe to user settings changes
    this.websocket.subscribe(channelName, Events.PracticeUserUserSettingsChanged, () => {
      // Reload user settings from UserStore
      this.events.trigger(new CoreEvent('user_settings_changed'));
    });

    // Subscribe to appointment types changes
    this.websocket.subscribe(channelName, Events.AppointmentAppointmentTypesChanged, () => {
      this.loadAppointmentTypes().subscribe();
    });

    // Subscribe to practice settings changes
    this.websocket.subscribe(channelName, Events.PracticeSettingsPracticeSettingChanged, () => {
      this.loadPracticeSettings().subscribe();
    });
  }

  /**
   * Reset WebSocket subscriptions
   */
  private resetPracticePushService(): void {
    const channelName = this.practiceEventsChannelName();
    this.websocket.unsubscribe(channelName, Events.PracticeUserUserSettingsChanged);
    this.websocket.unsubscribe(channelName, Events.AppointmentAppointmentTypesChanged);
    this.websocket.unsubscribe(channelName, Events.PracticeSettingsPracticeSettingChanged);
  }
}
